import React, { useState, useEffect } from 'react';
import { format, addDays, subDays } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { motion } from 'framer-motion';
import { useAuth } from '../../hooks/useAuth';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { journalService } from '../../services/journal/journal';
import { JournalEntry } from '../../services/journal/types';
import {
  PenLine, Sun, Moon, CloudRain, Music, Sparkles,
  Heading1, ListOrdered, ListTodo, Quote,
  BoldIcon, ItalicIcon, Underline, Highlighter,
  SeparatorHorizontal, Table, Timer, BookMarked,
  FileCheck, Tag, AlignLeft, AlignCenter, AlignRight,
  Link, Code, Wand2, Loader2, ChevronLeft, ChevronRight,
  Calendar
} from 'lucide-react';
import { analyzeJournal } from '../../services/ai/deepseek';

const TEMPLATE = `# Título do Diário

## O que rolou?

## Quais os planos?

## O que aprendeu hoje?

## No que vai melhorar amanhã?

---
`;

export const MontblancNotes: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [content, setContent] = useState('');
  const [mood, setMood] = useState('');
  const [weather, setWeather] = useState('');
  const [music, setMusic] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [lineCount, setLineCount] = useState(0);
  const [selectedText, setSelectedText] = useState('');
  const [textareaRef, setTextareaRef] = useState<HTMLTextAreaElement | null>(null);
  const [currentEntry, setCurrentEntry] = useState<JournalEntry | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<JournalEntry[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const lines = content.split('\n').length;
    setLineCount(lines);
  }, [content]);

  useEffect(() => {
    if (user) {
      loadEntry(selectedDate);
    }
  }, [user, selectedDate]);

  const loadEntry = async (date: Date) => {
    if (!user) return;

    try {
      const entry = await journalService.getEntryByDate(user.id, date);
      if (entry) {
        setCurrentEntry(entry);
        setContent(entry.content || '');
        setMood(entry.mood || '');
        setWeather(entry.weather || '');
        setMusic(entry.music || '');
      } else {
        setCurrentEntry(null);
        setContent(TEMPLATE);
        setMood('');
        setWeather('');
        setMusic('');
      }
    } catch (error: any) {
      console.error('Error loading journal entry:', error);
      toast.error('Erro ao carregar a entrada do diário');
    }
  };

  const handlePreviousDay = () => {
    setSelectedDate(prev => subDays(prev, 1));
  };

  const handleNextDay = () => {
    setSelectedDate(prev => addDays(prev, 1));
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  const handleSave = async () => {
    if (!user) {
      toast.error('Você precisa estar autenticado para salvar notas');
      return;
    }

    const trimmedContent = content?.trim();

    if (!trimmedContent) {
      toast.error('O conteúdo é obrigatório');
      return;
    }

    setIsSaving(true);
    try {
      const entryData = {
        content: trimmedContent,
        mood,
        weather,
        music,
        user_id: user.id,
        created_at: selectedDate.toISOString()
      };

      if (currentEntry) {
        await journalService.updateEntry(currentEntry.id, entryData);
        toast.success('Nota atualizada com sucesso!');
      } else {
        const newEntry = await journalService.createEntry(entryData);
        setCurrentEntry(newEntry);
        toast.success('Nota criada com sucesso!');
      }
    } catch (error: any) {
      console.error('Error saving journal entry:', error);
      toast.error(error.message || 'Erro ao salvar a nota');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAnalyzeDay = async () => {
    if (!content.trim()) return;

    setIsAnalyzing(true);
    try {
      const analysis = await analyzeJournal(content);
      setContent(prev => `${prev}\n\n---\n\n💭 Análise IA:\n${analysis}`);
      toast.success('Análise concluída!');
    } catch (error: any) {
      console.error('Error analyzing journal:', error);
      toast.error(error.message || 'Erro ao analisar o conteúdo');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const insertAtCursor = (before: string, after: string = '') => {
    if (!textareaRef) return;

    const start = textareaRef.selectionStart;
    const end = textareaRef.selectionEnd;
    const selection = content.substring(start, end);
    const newContent = content.substring(0, start) + before + selection + after + content.substring(end);
    
    setContent(newContent);
    
    setTimeout(() => {
      textareaRef.focus();
      textareaRef.setSelectionRange(
        start + before.length,
        start + before.length + selection.length
      );
    }, 0);
  };

  const useTemplate = () => {
    setContent(TEMPLATE);
  };

  const formattingOptions = [
    {
      id: 'title',
      icon: Heading1,
      label: 'Título',
      shortcut: '⌘+H',
      action: () => insertAtCursor('# ')
    },
    {
      id: 'subtitle',
      icon: Heading1,
      label: 'Subtítulo',
      shortcut: '⌘+⇧+H',
      action: () => insertAtCursor('## ')
    },
    {
      id: 'bold',
      icon: BoldIcon,
      label: 'Negrito',
      shortcut: '⌘+B',
      action: () => insertAtCursor('**', '**')
    },
    {
      id: 'italic',
      icon: ItalicIcon,
      label: 'Itálico',
      shortcut: '⌘+I',
      action: () => insertAtCursor('_', '_')
    },
    {
      id: 'underline',
      icon: Underline,
      label: 'Sublinhado',
      shortcut: '⌘+U',
      action: () => insertAtCursor('__', '__')
    },
    {
      id: 'highlight',
      icon: Highlighter,
      label: 'Destacar',
      shortcut: '⌘+⇧+H',
      action: () => insertAtCursor('==', '==')
    },
    {
      id: 'checklist',
      icon: ListTodo,
      label: 'Lista de Tarefas',
      shortcut: '⌘+⇧+L',
      action: () => insertAtCursor('- [ ] ')
    },
    {
      id: 'orderedList',
      icon: ListOrdered,
      label: 'Lista Numerada',
      shortcut: '⌘+⇧+N',
      action: () => insertAtCursor('1. ')
    },
    {
      id: 'quote',
      icon: Quote,
      label: 'Citação',
      shortcut: '⌘+⇧+.',
      action: () => insertAtCursor('> ')
    },
    {
      id: 'separator',
      icon: SeparatorHorizontal,
      label: 'Separador',
      action: () => insertAtCursor('\n---\n')
    },
    {
      id: 'table',
      icon: Table,
      label: 'Tabela',
      action: () => insertAtCursor('\n| Coluna 1 | Coluna 2 |\n|----------|----------|\n| Item 1   | Item 2   |\n')
    },
    {
      id: 'timer',
      icon: Timer,
      label: 'Temporizador',
      action: () => insertAtCursor('⏱️ ')
    },
    {
      id: 'bookmark',
      icon: BookMarked,
      label: 'Marcador',
      action: () => insertAtCursor('🔖 ')
    },
    {
      id: 'task',
      icon: FileCheck,
      label: 'Tarefa',
      action: () => insertAtCursor('📋 ')
    },
    {
      id: 'tag',
      icon: Tag,
      label: 'Tag',
      action: () => insertAtCursor('#')
    },
    {
      id: 'link',
      icon: Link,
      label: 'Link',
      action: () => insertAtCursor('[', '](url)')
    },
    {
      id: 'code',
      icon: Code,
      label: 'Código',
      action: () => insertAtCursor('`', '`')
    },
    {
      id: 'alignLeft',
      icon: AlignLeft,
      label: 'Alinhar à Esquerda',
      action: () => insertAtCursor('::: left\n', '\n:::')
    },
    {
      id: 'alignCenter',
      icon: AlignCenter,
      label: 'Centralizar',
      action: () => insertAtCursor('::: center\n', '\n:::')
    },
    {
      id: 'alignRight',
      icon: AlignRight,
      label: 'Alinhar à Direita',
      action: () => insertAtCursor('::: right\n', '\n:::')
    }
  ];

  const handleSearch = async (query: string) => {
    if (!user) return;
    
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await journalService.searchEntries(user.id, query.trim());
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching entries:', error);
      toast.error('Erro ao buscar entradas');
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectSearchResult = (entry: JournalEntry) => {
    setSelectedDate(new Date(entry.created_at));
    setSearchQuery('');
    setSearchResults([]);
  };

  return (
    <div className="w-full">
      <div className="bg-[rgb(28,28,30)] rounded-2xl p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-[28px] font-semibold text-white">Notas do Dia</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handlePreviousDay}
              className="bg-[rgb(44,44,46)] hover:bg-[rgb(60,60,62)] text-[rgb(152,152,157)] rounded-2xl p-2 transition-all duration-200"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={handleToday}
              className="bg-[rgb(44,44,46)] hover:bg-[rgb(60,60,62)] text-[rgb(152,152,157)] rounded-2xl py-2 px-3 text-sm font-medium transition-all duration-200"
            >
              Hoje
            </button>
            <button
              onClick={handleNextDay}
              className="bg-[rgb(44,44,46)] hover:bg-[rgb(60,60,62)] text-[rgb(152,152,157)] rounded-2xl p-2 transition-all duration-200"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-white hover:bg-white/90 text-black rounded-2xl py-3 px-4 text-sm font-medium transition-all duration-200 flex items-center gap-2"
            >
              {isSaving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileCheck className="w-4 h-4" />
              )}
              Salvar
            </button>
          </div>
        </div>

        {/* Data e Metadados */}
        <div className="flex items-center justify-between">
          <div className="text-[20px] font-semibold text-white">
            {format(selectedDate, "EEEE, d 'de' MMMM", { locale: ptBR })}
          </div>
          <div className="flex items-center gap-4">
            {/* Humor */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMood('morning')}
                className={`p-2 rounded-2xl transition-all duration-200 ${
                  mood === 'morning'
                    ? 'bg-[rgb(255,149,0,0.2)] text-[rgb(255,149,0)]'
                    : 'bg-[rgb(44,44,46)] text-[rgb(152,152,157)] hover:bg-[rgb(60,60,62)]'
                }`}
              >
                <Sun className="w-4 h-4" />
              </button>
              <button
                onClick={() => setMood('evening')}
                className={`p-2 rounded-2xl transition-all duration-200 ${
                  mood === 'evening'
                    ? 'bg-[rgb(88,86,214,0.2)] text-[rgb(88,86,214)]'
                    : 'bg-[rgb(44,44,46)] text-[rgb(152,152,157)] hover:bg-[rgb(60,60,62)]'
                }`}
              >
                <Moon className="w-4 h-4" />
              </button>
              <button
                onClick={() => setMood('inspiration')}
                className={`p-2 rounded-2xl transition-all duration-200 ${
                  mood === 'inspiration'
                    ? 'bg-[rgb(52,199,89,0.2)] text-[rgb(52,199,89)]'
                    : 'bg-[rgb(44,44,46)] text-[rgb(152,152,157)] hover:bg-[rgb(60,60,62)]'
                }`}
              >
                <Sparkles className="w-4 h-4" />
              </button>
            </div>

            {/* Clima */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setWeather('sunny')}
                className={`p-2 rounded-2xl transition-all duration-200 ${
                  weather === 'sunny'
                    ? 'bg-[rgb(255,149,0,0.2)] text-[rgb(255,149,0)]'
                    : 'bg-[rgb(44,44,46)] text-[rgb(152,152,157)] hover:bg-[rgb(60,60,62)]'
                }`}
              >
                <Sun className="w-4 h-4" />
              </button>
              <button
                onClick={() => setWeather('rainy')}
                className={`p-2 rounded-2xl transition-all duration-200 ${
                  weather === 'rainy'
                    ? 'bg-[rgb(0,122,255,0.2)] text-[rgb(0,122,255)]'
                    : 'bg-[rgb(44,44,46)] text-[rgb(152,152,157)] hover:bg-[rgb(60,60,62)]'
                }`}
              >
                <CloudRain className="w-4 h-4" />
              </button>
            </div>

            {/* Música */}
            <button
              onClick={() => setMusic(music ? '' : 'playing')}
              className={`p-2 rounded-2xl transition-all duration-200 ${
                music
                  ? 'bg-[rgb(88,86,214,0.2)] text-[rgb(88,86,214)]'
                  : 'bg-[rgb(44,44,46)] text-[rgb(152,152,157)] hover:bg-[rgb(60,60,62)]'
              }`}
            >
              <Music className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Editor */}
        <div className="space-y-4">
          {/* Barra de Formatação */}
          <div className="flex items-center gap-2 p-2 bg-[rgb(44,44,46)] rounded-2xl">
            {formattingOptions.map((option) => (
              <button
                key={option.id}
                onClick={option.action}
                className="p-2 rounded-2xl text-[rgb(152,152,157)] hover:bg-[rgb(60,60,62)] transition-all duration-200"
                title={`${option.label} ${option.shortcut}`}
              >
                <option.icon className="w-4 h-4" />
              </button>
            ))}
          </div>

          {/* Área de Texto com Preview */}
          <div className="grid grid-cols-2 gap-4">
            {/* Editor */}
            <div className="relative">
              <textarea
                ref={(ref) => setTextareaRef(ref)}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="w-full h-[500px] p-4 bg-[rgb(44,44,46)] rounded-2xl text-[17px] text-white resize-none focus:outline-none focus:ring-2 focus:ring-[rgb(0,122,255)] transition-all duration-200"
                placeholder="Comece a escrever..."
              />
              <div className="absolute bottom-4 right-4 text-sm text-[rgb(152,152,157)]">
                {lineCount} linhas
              </div>
            </div>

            {/* Preview */}
            <div className="relative">
              <div className="w-full h-[500px] p-4 bg-[rgb(44,44,46)] rounded-2xl overflow-y-auto">
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  className="prose prose-invert max-w-none text-[17px] text-white"
                >
                  {content || '*Comece a escrever...*'}
                </ReactMarkdown>
              </div>
            </div>
          </div>

          {/* Ações */}
          <div className="flex items-center justify-between">
            <button
              onClick={useTemplate}
              className="bg-[rgb(44,44,46)] hover:bg-[rgb(60,60,62)] text-[rgb(152,152,157)] rounded-2xl py-2 px-3 text-sm font-medium transition-all duration-200 flex items-center gap-2"
            >
              <BookMarked className="w-4 h-4" />
              Usar Template
            </button>
            <button
              onClick={handleAnalyzeDay}
              disabled={isAnalyzing}
              className="bg-[rgb(44,44,46)] hover:bg-[rgb(60,60,62)] text-[rgb(152,152,157)] rounded-2xl py-2 px-3 text-sm font-medium transition-all duration-200 flex items-center gap-2"
            >
              {isAnalyzing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Wand2 className="w-4 h-4" />
              )}
              Analisar Dia
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
